import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
    deleteFilterPreset,
    listFilterPresets,
    saveFilterPreset,
} from '../../services/statsFilterPresets';
import { show_app_toast } from '../../services/appToast';

import './StatsFilterPresetsControl.scss';

/**
 * Filter-preset toolbar: select applies; one "Manage" button opens CRUD modal.
 * Persists via Kore instance_config — parent supplies capture + apply.
 */
function StatsFilterPresetsControl({
    scope,
    tktInstanceId,
    captureFilters,
    onApply,
    disabled,
}) {
    const { t } = useTranslation();
    const [presets, setPresets] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);
    const [manageTargetId, setManageTargetId] = useState('');
    const [manageName, setManageName] = useState('');

    const canUse = Boolean(tktInstanceId) && Boolean(scope) && !disabled;

    const refresh = useCallback(async () => {
        if (!tktInstanceId || !scope) {
            setPresets([]);
            return;
        }
        setLoading(true);
        try {
            const list = await listFilterPresets(tktInstanceId, scope);
            setPresets(list);
        } catch (err) {
            show_app_toast(err?.message || t('stats.filter_presets.load_error'), { variant: 'error' });
            setPresets([]);
        } finally {
            setLoading(false);
        }
    }, [tktInstanceId, scope, t]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const closeManage = () => {
        setManageOpen(false);
        setManageTargetId('');
        setManageName('');
    };

    const openManage = () => {
        const target = selectedId || '';
        const preset = presets.find((p) => p.id === target);
        setManageTargetId(target);
        setManageName(preset?.name || '');
        setManageOpen(true);
    };

    const onSelectChange = (e) => {
        const id = e.target.value;
        setSelectedId(id);
        if (!id) return;
        const preset = presets.find((p) => p.id === id);
        if (preset && typeof onApply === 'function') {
            onApply(preset);
        }
    };

    const onManageTargetChange = (e) => {
        const id = e.target.value;
        setManageTargetId(id);
        if (!id) {
            setManageName('');
            return;
        }
        const preset = presets.find((p) => p.id === id);
        setManageName(preset?.name || '');
    };

    const onSaveFromModal = async () => {
        const name = String(manageName || '').trim();
        if (!name || !canUse || busy) return;

        setBusy(true);
        try {
            const filters = typeof captureFilters === 'function' ? captureFilters() : {};
            const { presets: next, preset } = await saveFilterPreset(tktInstanceId, scope, {
                id: manageTargetId || undefined,
                name,
                filters,
            });
            setPresets(next);
            setSelectedId(preset.id);
            setManageTargetId(preset.id);
            show_app_toast(t('stats.filter_presets.saved'), { variant: 'success' });
            closeManage();
        } catch (err) {
            show_app_toast(err?.message || t('stats.filter_presets.save_error'), { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const onDeleteFromModal = async () => {
        if (!manageTargetId || !canUse || busy) return;
        const target = presets.find((p) => p.id === manageTargetId);
        if (!window.confirm(t('stats.filter_presets.delete_confirm', { name: target?.name || '' }))) {
            return;
        }
        setBusy(true);
        try {
            const next = await deleteFilterPreset(tktInstanceId, scope, manageTargetId);
            setPresets(next);
            if (selectedId === manageTargetId) {
                setSelectedId('');
            }
            show_app_toast(t('stats.filter_presets.deleted'), { variant: 'success' });
            closeManage();
        } catch (err) {
            show_app_toast(err?.message || t('stats.filter_presets.save_error'), { variant: 'error' });
        } finally {
            setBusy(false);
        }
    };

    return (
        <span className="StatsFilterPresetsControl d-inline-flex align-items-center flex-nowrap">
            <select
                className="custom-select custom-select-sm StatsFilterPresetsControl__select"
                value={selectedId}
                onChange={onSelectChange}
                disabled={!canUse || loading || busy}
                aria-label={t('stats.filter_presets.select_label')}
                title={t('stats.filter_presets.select_label')}
            >
                <option value="">{t('stats.filter_presets.none')}</option>
                {presets.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                ))}
            </select>
            <Button
                type="button"
                size="sm"
                color="secondary"
                outline
                className="FiltersCard__toolbar-btn FiltersCard__header-tool StatsFilterPresetsControl__manage"
                onClick={openManage}
                disabled={!canUse || busy}
                title={t('stats.filter_presets.manage')}
                aria-label={t('stats.filter_presets.manage')}
            >
                <i className="fa fa-bookmark" aria-hidden="true" />
            </Button>

            <Modal isOpen={manageOpen} toggle={closeManage} centered>
                <ModalHeader toggle={closeManage}>
                    {t('stats.filter_presets.manage_title')}
                </ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="stats-filter-preset-target">
                            {t('stats.filter_presets.target_label')}
                        </Label>
                        <Input
                            id="stats-filter-preset-target"
                            type="select"
                            value={manageTargetId}
                            onChange={onManageTargetChange}
                            disabled={busy}
                        >
                            <option value="">{t('stats.filter_presets.target_new')}</option>
                            {presets.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </Input>
                    </FormGroup>
                    <FormGroup className="mb-0">
                        <Label for="stats-filter-preset-name">
                            {t('stats.filter_presets.name_placeholder')}
                        </Label>
                        <Input
                            id="stats-filter-preset-name"
                            type="text"
                            value={manageName}
                            onChange={(e) => setManageName(e.target.value)}
                            placeholder={t('stats.filter_presets.name_placeholder')}
                            autoFocus
                            disabled={busy}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    onSaveFromModal();
                                }
                            }}
                        />
                    </FormGroup>
                    {manageTargetId ? (
                        <p className="text-muted small mt-2 mb-0">
                            {t('stats.filter_presets.save_overwrite_hint')}
                        </p>
                    ) : (
                        <p className="text-muted small mt-2 mb-0">
                            {t('stats.filter_presets.save_new_hint')}
                        </p>
                    )}
                </ModalBody>
                <ModalFooter className="StatsFilterPresetsControl__modal-footer">
                    {manageTargetId ? (
                        <Button
                            color="danger"
                            outline
                            className="mr-auto"
                            onClick={onDeleteFromModal}
                            disabled={busy}
                        >
                            {t('stats.filter_presets.delete')}
                        </Button>
                    ) : null}
                    <Button color="secondary" outline onClick={closeManage} disabled={busy}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        color="primary"
                        onClick={onSaveFromModal}
                        disabled={busy || !String(manageName || '').trim()}
                    >
                        {t('stats.filter_presets.save')}
                    </Button>
                </ModalFooter>
            </Modal>
        </span>
    );
}

StatsFilterPresetsControl.propTypes = {
    scope: PropTypes.string.isRequired,
    tktInstanceId: PropTypes.string,
    captureFilters: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

StatsFilterPresetsControl.defaultProps = {
    tktInstanceId: '',
    disabled: false,
};

export default StatsFilterPresetsControl;
